import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import { useSessionStore } from '@/stores/useSessionStore'
import type { ApiResponse } from '@/api/http/types'

/** Row from platform `GET /api/v1/team` (Sensemaker ADMIN). */
export interface TeamMemberRow {
  readonly _id: string
  readonly firstName: string
  readonly lastName: string
  readonly email: string
  readonly role: string
  readonly status: string
  readonly platformUserId?: string | null
}

function tenantKey(tenantId: string | null | undefined) {
  return tenantId ?? '_'
}

/**
 * Active team members for campaign assignment (paginates until all loaded, max 100 per page).
 */
export function useTeamMembersForAssignment() {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.team.activeMembers(tenantKey(tenantId)),
    queryFn: async () => {
      const collected: TeamMemberRow[] = []
      let page = 1
      let totalPages = 1
      do {
        const { data: envelope } = await apiClient.get<
          ApiResponse<TeamMemberRow[]>
        >('/api/v1/team', {
          params: {
            page,
            pageSize: 100,
            status: 'active',
          },
          // Blocked tenant / auth errors must surface on the page — not redirect via interceptor.
          skipUnauthorizedRedirect: true,
        })
        collected.push(...envelope.data)
        const tp = envelope.meta?.total_pages
        totalPages =
          typeof tp === 'number' && Number.isFinite(tp) ? Math.max(1, tp) : 1
        page += 1
      } while (page <= totalPages)
      return collected
    },
    enabled: isAuthenticated,
  })
}
